import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { dkk, num, formatErr } from "../lib/api";
import {
  Car, Wrench, TrendingUp, Gauge, AlertTriangle, Tag, Package,
  ClipboardCheck, Hammer, ListChecks, Inbox,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  const s = stats;
  const hasLoss = s?.sold_loss > 0;

  const monthName = (label) => {
    if (!label) return "";
    const [mm, yyyy] = label.split("/");
    const NAMES = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];
    const idx = parseInt(mm, 10) - 1;
    return idx >= 0 && idx < 12 ? `${NAMES[idx]} ${yyyy}` : label;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]" data-testid="dashboard-page">
      <div className="overline text-zinc-500">Overblik</div>
      <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2 mb-1">Din flåde, lige nu.</h1>
      <p className="text-zinc-400 text-xs sm:text-sm mb-6 sm:mb-10">
        Nøgletal fra alle biler — udlejning, salg og drift.
      </p>

      {/* TOP KPI — Samlet indtjening + samlet omkostninger + avance (snit pr. måned) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-10">
        <TopKPI
          icon={TrendingUp}
          label="Samlet indtjening · snit pr. måned"
          value={s ? dkk(s.avg_earnings_monthly) : "—"}
          subLabel="GoMore + Carbuddi + bilsalg · ex. moms"
          subValue={s ? `${num(s.months_elapsed)} md.` : ""}
          accent="gold"
          testid="kpi-avg-earnings"
        />
        <TopKPI
          icon={Receipt}
          label="Samlet omkostninger · snit pr. måned"
          value={s ? dkk(s.avg_costs_monthly) : "—"}
          subLabel="Firma · faste + variable · ex. moms"
          subValue={s ? `${num(s.months_elapsed)} md.` : ""}
          accent="red"
          testid="kpi-avg-costs"
        />
        <TopKPI
          icon={Gauge}
          label="Gennemsnit avance · snit pr. måned"
          value={s ? dkk((s.avg_earnings_monthly || 0) - (s.avg_costs_monthly || 0)) : "—"}
          subLabel="Indtjening − omkostninger · ex. moms"
          subValue={s ? `${num(s.months_elapsed)} md.` : ""}
          accent={s && ((s.avg_earnings_monthly || 0) - (s.avg_costs_monthly || 0)) >= 0 ? "emerald" : "red"}
          testid="kpi-avg-profit"
        />
      </div>

      {/* Operations alerts — 4 bokse: værksted, opgaver, kontrol, forespørgsler */}
      <SectionTitle>Drift & vedligehold</SectionTitle>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-2">
        <AlertKPI
          icon={Hammer}
          label="Til værksted"
          value={s ? num(s.cars_to_workshop) : "—"}
          warn={s?.cars_to_workshop > 0}
          to="/admin/opgaver"
          testid="kpi-workshop"
        />
        <AlertKPI
          icon={ListChecks}
          label="Mine opgaver"
          value={s ? num(s.open_tasks) : "—"}
          warn={s?.open_tasks > 0}
          to="/admin/opgaver"
          testid="kpi-open-tasks"
        />
        <AlertKPI
          icon={ClipboardCheck}
          label="Overskredet kontrol"
          value={
            s
              ? s.overdue_inspections === 0 && s.closest_inspection_days_remaining != null
                ? (
                    <>
                      0
                      <span className="text-sm font-normal tracking-normal text-zinc-500 ml-2">
                        Næste om {num(Math.max(0, s.closest_inspection_days_remaining))} dage
                      </span>
                    </>
                  )
                : num(s.overdue_inspections)
              : "—"
          }
          warn={s?.overdue_inspections > 0}
          to="/admin/kontrol"
          testid="kpi-overdue"
        />
        <AlertKPI
          icon={Inbox}
          label="Forespørgsler"
          value={s ? num(s.pending_bookings) : "—"}
          warn={s?.pending_bookings > 0}
          to="/admin/forespoergsler"
          testid="kpi-bookings"
        />
      </div>
      {s && s.cars_due_service > 0 && (
        <div className="text-xs text-amber-400 mb-6 sm:mb-10" data-testid="service-hint">
          <Wrench className="w-3 h-3 inline mr-1" />
          Skal til service: {num(s.cars_due_service)} bil{s.cars_due_service === 1 ? "" : "er"}
          {" "}<Link to="/admin/kontrol" className="underline hover:text-amber-300">→ se kontrol</Link>
        </div>
      )}
      {(!s || s.cars_due_service === 0) && <div className="mb-6 sm:mb-10" />}

      {/* Omkostninger — gennemsnit pr. måned (5 dele) */}
      <SectionTitle>Omkostninger</SectionTitle>
      {(() => {
        const now = new Date();
        const monthsElapsed = Math.max(1, now.getMonth() + 1);
        const firmFixedYtd = s?.fixed_costs_ytd || 0;
        const firmVarYtd = s?.variable_costs_ytd || 0;
        const firmTotalAvg = (firmFixedYtd + firmVarYtd) / monthsElapsed;
        const gomoreFixed = s?.car_fixed_costs_monthly || 0;
        const carbuddiFixed = s?.carbuddi_fixed_costs_monthly || 0;
        const lagerFixed = s?.lager_fixed_costs_monthly || 0;
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-8">
            <CostKPI
              icon={Receipt}
              label="Firma"
              value={s ? dkk(firmTotalAvg) : "—"}
              subValue="snit/md"
              subLabel="faste + variable"
              to="/admin/omkostninger"
              testid="kpi-firm-monthly"
            />
            <CostKPI
              icon={Car}
              label="GoMore biler"
              value={s ? dkk(gomoreFixed) : "—"}
              subValue="snit/md"
              subLabel="faste"
              to="/admin/biler"
              testid="kpi-gomore-fixed"
            />
            <CostKPI
              icon={Car}
              label="Carbuddi biler"
              value={s ? dkk(carbuddiFixed) : "—"}
              subValue="snit/md"
              subLabel="faste"
              to="/admin/carbuddi-biler"
              testid="kpi-carbuddi-fixed"
            />
            <CostKPI
              icon={Package}
              label="Lager biler"
              value={s ? dkk(lagerFixed) : "—"}
              subValue="snit/md"
              subLabel="faste"
              to="/admin/lager"
              testid="kpi-lager-fixed"
            />
          </div>
        );
      })()}

      <SectionTitle>Flåde</SectionTitle>
      {/* Øverste række — 3 cols altid */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-2 sm:mb-3">
        <FleetCount
          icon={Car}
          label="Aktive biler"
          value={s ? num(s.total_cars) : "—"}
          to="/admin/biler"
          testid="kpi-total-cars"
        />
        <FleetCount
          icon={Package}
          label="På lager"
          value={s ? num(s.lager_cars) : "—"}
          to="/admin/lager"
          testid="kpi-lager-cars"
        />
        <FleetCount
          icon={Tag}
          label="Solgte i alt"
          value={s ? num(s.sold_cars) : "—"}
          to="/admin/solgte"
          testid="kpi-sold-cars"
        />
      </div>

      {/* Nederste række — også 3 cols altid, samme størrelse */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
        <FleetCount
          icon={TrendingUp}
          label="Solgt med fortjeneste"
          value={s ? num(s.sold_with_profit_count || 0) : "—"}
          sub={s && s.sold_profit ? dkk(s.sold_profit) : null}
          to="/admin/solgte"
          testid="kpi-sold-profit"
          color="emerald"
        />
        <FleetCount
          icon={AlertTriangle}
          label="Solgt med tab"
          value={s ? num(s.sold_with_loss_count || 0) : "—"}
          sub={s && s.sold_loss ? `−${dkk(s.sold_loss)}` : null}
          to="/admin/solgte"
          testid="kpi-sold-loss"
          color="red"
        />
        <FleetCount
          icon={Gauge}
          label="Kørt kilometer"
          value={s ? `${num(s.ytd_km)} km` : "—"}
          sub="i år, fra udlejning"
          testid="kpi-ytd-km"
        />
      </div>

      <SectionTitle>Flådeværdi (købspris)</SectionTitle>
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <FleetCount
          icon={Car}
          label="Aktive"
          value={s ? dkk(s.active_value) : "—"}
          sub={s && s.total_cars > s.active_with_price ? `${s.total_cars - s.active_with_price} mangler købspris` : null}
          to="/admin/biler"
          testid="kpi-active-value"
        />
        <FleetCount
          icon={Package}
          label="Lager"
          value={s ? dkk(s.lager_value) : "—"}
          sub={s && s.lager_cars > s.lager_with_price ? `${s.lager_cars - s.lager_with_price} mangler købspris` : null}
          to="/admin/lager"
          testid="kpi-lager-value"
        />
        <FleetCount
          icon={Receipt}
          label="Samlet"
          value={s ? dkk(s.total_fleet_value) : "—"}
          to="/admin/lager"
          testid="kpi-total-fleet-value"
          gold
        />
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="overline text-zinc-500 mb-3">{children}</div>;
}

function TopKPI({ icon: Icon, label, value, subLabel, subValue, accent, testid }) {
  const borderClass = accent === "gold" ? "border-l-[#C8A661]" : accent === "red" ? "border-l-red-500" : accent === "emerald" ? "border-l-emerald-500" : "border-l-zinc-700";
  const iconClass = accent === "gold" ? "text-[#C8A661]" : accent === "red" ? "text-red-400" : accent === "emerald" ? "text-emerald-400" : "text-zinc-500";
  const valueClass = accent === "gold" ? "text-[#C8A661]" : accent === "red" ? "text-red-400" : accent === "emerald" ? "text-emerald-400" : "text-white";
  return (
    <div className={"kpi-card border-l-2 " + borderClass + " p-5 sm:p-7"} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-[10px] sm:text-xs">{label}</div>
        <Icon className={"w-5 h-5 " + iconClass} strokeWidth={2} />
      </div>
      <div className={"font-display font-black tracking-tighter text-3xl sm:text-5xl lg:text-6xl " + valueClass}>
        {value}
      </div>
      <div className="text-[10px] sm:text-xs text-zinc-500 mt-2 flex items-center justify-between gap-3">
        <span>{subLabel}</span>
        {subValue && <span className="text-zinc-600">{subValue}</span>}
      </div>
    </div>
  );
}

function AlertKPI({ icon: Icon, label, value, sub, warn, to, testid }) {
  const content = (
    <div
      className={
        "kpi-card transition-colors hover:bg-[#262626] p-3 sm:p-6 " +
        (warn ? "border-l-2 border-l-red-500" : "")
      }
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-2 sm:mb-4 gap-1">
        <div className="overline text-[9px] sm:text-xs leading-tight">{label}</div>
        <Icon className={"w-3 h-3 sm:w-4 sm:h-4 shrink-0 " + (warn ? "text-red-400" : "text-zinc-500")} strokeWidth={2} />
      </div>
      <div className={"font-display font-black tracking-tighter text-2xl sm:text-4xl " + (warn ? "text-red-400" : "text-white")}>
        {value}
      </div>
      {sub && <div className="text-[10px] sm:text-xs text-zinc-500 mt-1 sm:mt-2">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function FleetCount({ icon: Icon, label, value, sub, to, testid, gold, color, compact }) {
  const valueColor =
    color === "emerald" ? "text-emerald-400" :
    color === "red" ? "text-red-400" :
    gold ? "text-[#C8A661]" : "text-white";
  const iconColor =
    color === "emerald" ? "text-emerald-500" :
    color === "red" ? "text-red-500" :
    "text-zinc-600";
  const padding = compact ? "p-2 sm:p-3" : "p-3 sm:p-5";
  const valueSize = compact ? "text-base sm:text-2xl" : "text-lg sm:text-3xl";
  const labelMargin = compact ? "mb-0.5 sm:mb-1" : "mb-1 sm:mb-2";
  const iconSize = compact ? "w-3 h-3 sm:w-4 sm:h-4" : "w-4 h-4 sm:w-6 sm:h-6";
  const Wrapper = to ? Link : "div";
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={"section-card flex items-center justify-between transition-colors " + padding + " " + (to ? "hover:bg-[#262626]" : "")}
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className={"overline text-[9px] sm:text-xs leading-tight " + labelMargin}>{label}</div>
        <div className={"font-display font-black tracking-tighter truncate " + valueSize + " " + valueColor}>
          {value}
        </div>
        {sub && <div className={"text-[9px] sm:text-[10px] mt-0.5 sm:mt-1 " + (color === "red" ? "text-red-400/80" : color === "emerald" ? "text-emerald-400/80" : "text-zinc-500")}>{sub}</div>}
      </div>
      <Icon className={"shrink-0 ml-2 " + iconSize + " " + iconColor} strokeWidth={1.5} />
    </Wrapper>
  );
}

function CostKPI({ icon: Icon, label, value, subValue, subLabel, to, testid, accent }) {
  return (
    <Link
      to={to}
      className={"bg-[#212121] border " + (accent ? "border-[#C8A661]/40 border-l-2 border-l-[#C8A661]" : "border-[#383838]") + " rounded-[2px] transition-colors hover:bg-[#262626] block p-3 sm:p-4"}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[9px] sm:text-xs">{label}</div>
        <Icon className={"w-3 h-3 sm:w-4 sm:h-4 " + (accent ? "text-[#C8A661]" : "text-zinc-500")} strokeWidth={2} />
      </div>
      <div className={"font-display font-black text-lg sm:text-xl tracking-tighter " + (accent ? "text-[#C8A661]" : "text-white")}>{value}</div>
      <div className="text-[10px] sm:text-xs text-zinc-500 mt-1">
        {subLabel}: <span className="text-emerald-400">{subValue}</span>
      </div>
    </Link>
  );
}
